import {
  Badge,
  Banner,
  Button,
  ClipboardText,
  DeleteResource,
  Dialog,
  Empty,
  Grid,
  GridItem,
  Input,
  Loader,
  Meter,
  Select,
  Surface,
  Table,
  Text,
  type BadgeVariant,
} from '@cloudflare/kumo'
import {
  ChatText,
  Gift,
  MagnifyingGlass,
  ShieldCheck,
  ShieldSlash,
  Trash,
  UserCircle,
} from '@phosphor-icons/react'
import {
  productCategoryLabel,
  type AdminCreditGrant,
  type AdminEntry,
  type AdminUserSimulation,
  type AdminUserView,
  type Entitlements,
  type Plan,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { adminApi } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { MessageDialog } from '../components/message-dialog'
import { PageHeader } from '../components/page-header'
import { PaymentLedgerPanel } from '../components/payment-ledger-panel'
import { ConfirmedEventsPanel } from '../components/confirmed-events-panel'
import { useGetToken } from '../lib/auth'
import { fmtDate, fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

const PLAN_BADGE: Record<Plan, BadgeVariant> = { free: 'outline', basic: 'blue', pro: 'purple' }
const PLAN_LABEL: Record<Plan, string> = { free: 'Free', basic: 'Basic', pro: 'Pro' }
const CREDIT_ITEMS = [
  { label: '공장 견적 크레딧', value: 'quote' },
  { label: '제품 생성 횟수', value: 'generations' },
]

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Text variant="secondary" size="xs">
        {label}
      </Text>
      <div className="min-w-0 truncate text-sm">{children}</div>
    </div>
  )
}

function PlanBadge({ plan, trial }: { plan: Plan; trial?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={PLAN_BADGE[plan]}>{PLAN_LABEL[plan]}</Badge>
      {trial && <Badge variant="warning">체험 중</Badge>}
    </span>
  )
}

/** 이번 기간 사용량 */
function Usage({ e }: { e: Entitlements }) {
  const pct = (used: number, limit: number) => (limit > 0 ? Math.min(100, (used / limit) * 100) : 0)
  const period = e.periodStart
    ? `${fmtDate(e.periodStart)} – ${fmtDate(e.periodEnd)}`
    : '평생 (초기화 없음)'
  return (
    <div className="flex flex-col gap-4" data-testid="user-usage">
      <Text variant="secondary" size="xs">
        이번 기간 · {period}
      </Text>
      <Grid variant="3up" gap="sm">
        <GridItem>
          <Meter
            label="제품 생성"
            value={pct(e.generations.used, e.generations.limit)}
            customValue={`${e.generations.used} / ${e.generations.limit}${
              e.generations.packRemaining > 0 ? ` (+추가 ${e.generations.packRemaining})` : ''
            }`}
          />
        </GridItem>
        <GridItem>
          <Meter
            label="AI 수정"
            value={pct(e.turns.used, e.turns.limit)}
            customValue={
              e.plan === 'free' ? `제품당 ${e.turns.limit}턴` : `${e.turns.used} / ${e.turns.limit}`
            }
          />
        </GridItem>
        <GridItem>
          {e.regenerations.limit > 0 ? (
            <Meter
              label="사진 다시 생성"
              value={pct(e.regenerations.used, e.regenerations.limit)}
              customValue={`${e.regenerations.used} / ${e.regenerations.limit}`}
            />
          ) : (
            <Fact label="사진 다시 생성">이 플랜에는 없어요</Fact>
          )}
        </GridItem>
      </Grid>
      <Fact label="견적 크레딧">
        <span data-testid="quote-credits">{e.quoteCredits.available}</span>개 남음
      </Fact>
    </div>
  )
}

/** 크레딧 지급 창 — 종류 · 수량 · 사유(작업 기록에 남는다) */
function GrantDialog({
  open,
  busy,
  onGrant,
  onClose,
}: {
  open: boolean
  busy: boolean
  onGrant: (g: AdminCreditGrant) => void
  onClose: () => void
}) {
  const [kind, setKind] = useState<AdminCreditGrant['kind']>('quote')
  const [amount, setAmount] = useState(1)
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (open) {
      setKind('quote')
      setAmount(1)
      setReason('')
    }
  }, [open])
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog size="base" className="p-6">
        <Dialog.Title className="text-lg font-semibold">크레딧 지급</Dialog.Title>
        <Dialog.Description className="text-kumo-subtle mt-1">
          보상·환불 대응용이에요. 지급 내역과 사유는 작업 기록에 남아요.
        </Dialog.Description>
        <div className="mt-5 flex flex-col gap-4">
          <Select
            label="종류"
            value={kind}
            onValueChange={(v: string | null) => v && setKind(v as AdminCreditGrant['kind'])}
            items={CREDIT_ITEMS}
          />
          <Input
            label="수량"
            description={
              kind === 'quote'
                ? '견적 요청 1건에 1개를 써요. 만료 없음.'
                : '월 한도를 다 쓴 뒤에 쓰이는 추가 생성 횟수. 만료 없음.'
            }
            type="number"
            min={1}
            max={100}
            value={String(amount)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAmount(Math.max(1, Math.min(100, Math.round(Number(e.target.value) || 1))))
            }
          />
          <Input
            label="사유"
            placeholder="예: 견적 지연 보상"
            maxLength={200}
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            icon={<Gift />}
            loading={busy}
            disabled={!reason.trim()}
            onClick={() => onGrant({ kind, amount, reason: reason.trim() })}
          >
            지급
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  )
}

/** 사용자 한 명의 카드 — 플랜·사용량·제품·지원 도구 */
function UserCard({
  user,
  onAdminChange,
  onDeleted,
}: {
  user: AdminUserView
  onAdminChange: (email: string, make: boolean) => Promise<void>
  /** warnings: 서버가 돌려준 삭제 경고 ('revenuecat') — 카드가 사라지므로 상위 화면이 보여 준다 */
  onDeleted: (userId: string, warnings: string[]) => void
}) {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [ent, setEnt] = useState<Entitlements | 'error' | null>(null)
  const [sims, setSims] = useState<AdminUserSimulation[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [grantOpen, setGrantOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  const [revoke, setRevoke] = useState<AdminCreditGrant['kind'] | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const pagedSims = usePagedList(sims, 5)

  useEffect(() => {
    let alive = true
    adminApi.users
      .entitlements(getToken, user.userId)
      .then((r) => alive && setEnt(r.entitlements))
      .catch(() => alive && setEnt('error'))
    adminApi.users
      .simulations(getToken, user.userId)
      .then((r) => alive && setSims(r.items))
      .catch(() => alive && setSims([]))
    return () => {
      alive = false
    }
  }, [getToken, user.userId])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const grant = (g: AdminCreditGrant) =>
    run(async () => {
      const r = await adminApi.users.grantCredits(getToken, user.userId, g)
      setEnt(r.entitlements)
      setGrantOpen(false)
      setNotice(`${g.kind === 'quote' ? '견적 크레딧' : '생성 횟수'} ${g.amount}개를 지급했어요.`)
    })
  const doRevoke = () =>
    run(async () => {
      if (!revoke) return
      const r = await adminApi.users.revokeCredits(getToken, user.userId, revoke)
      setEnt(r.entitlements)
      setRevoke(null)
      setNotice(
        r.removed > 0 ? `안 쓴 지급분 ${r.removed}건을 회수했어요.` : '회수할 지급분이 없어요.',
      )
    })
  const message = (msg: { title: string; body: string }) =>
    run(async () => {
      await adminApi.users.message(getToken, user.userId, msg)
      setMessageOpen(false)
      setNotice('메시지를 보냈어요.')
    })
  const remove = () =>
    run(async () => {
      let warnings: string[]
      try {
        warnings = (await adminApi.users.remove(getToken, user.userId)).warnings ?? []
      } catch (err) {
        // 서버가 503 ACCOUNT_DELETE_INCOMPLETE 를 주면 다시 눌러 이어서 지울 수 있다 (lib/account.ts)
        throw new Error(
          `계정 삭제가 끝나지 않았어요. 다시 누르면 남은 부분부터 이어서 지워요. (${toMessage(err)})`,
          { cause: err },
        )
      }
      setDeleteOpen(false)
      onDeleted(user.userId, warnings)
    })

  return (
    <div
      className="border-kumo-line flex flex-col gap-5 rounded-lg border p-5"
      data-testid="user-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text variant="heading">{user.name || user.email || user.userId}</Text>
            <PlanBadge plan={user.plan} trial={user.periodType === 'trial'} />
            {user.isAdmin && <Badge variant="success">어드민 · {user.adminSource}</Badge>}
          </div>
          <Text variant="secondary" size="sm">
            {user.email || '이메일 정보 없음'}
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            icon={<ChatText />}
            disabled={busy}
            onClick={() => setMessageOpen(true)}
          >
            메시지
          </Button>
          <Button
            variant="secondary"
            icon={<Gift />}
            disabled={busy}
            onClick={() => setGrantOpen(true)}
          >
            크레딧 지급
          </Button>
          {user.email &&
            (user.isAdmin ? (
              user.adminSource === 'table' && (
                <Button
                  variant="secondary"
                  icon={<ShieldSlash />}
                  disabled={busy}
                  onClick={() => void onAdminChange(user.email, false)}
                >
                  어드민 해제
                </Button>
              )
            ) : (
              <Button
                variant="secondary"
                icon={<ShieldCheck />}
                disabled={busy}
                onClick={() => void onAdminChange(user.email, true)}
              >
                어드민으로
              </Button>
            ))}
        </div>
      </div>

      {error && <Banner variant="error" size="sm" title="문제가 생겼어요" description={error} />}
      {notice && <Banner variant="default" size="sm" title={notice} />}

      <Grid variant="3up" gap="sm">
        <GridItem>
          <Fact label="사용자 ID">
            <ClipboardText text={user.userId} className="font-mono text-xs" />
          </Fact>
        </GridItem>
        <GridItem>
          <Fact label="가입">
            {user.createdAt.startsWith('1970') ? '—' : fmtDate(user.createdAt)}
          </Fact>
        </GridItem>
        <GridItem>
          <Fact label={user.periodType === 'trial' ? '체험 종료' : '구독 만료'}>
            {user.plan === 'free' ? '—' : fmtDate(user.periodEnd)}
          </Fact>
        </GridItem>
      </Grid>

      {ent === null ? (
        <div className="flex items-center gap-3">
          <Loader />
          <Text variant="secondary" size="sm">
            사용량을 불러오는 중…
          </Text>
        </div>
      ) : ent === 'error' ? (
        <Banner variant="alert" size="sm" title="사용량을 불러오지 못했어요" />
      ) : (
        <Usage e={ent} />
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Text variant="heading">만든 제품 {sims ? `${sims.length}개` : ''}</Text>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/simulations?userId=${encodeURIComponent(user.userId)}`)}
          >
            시뮬레이션에서 전부 보기
          </Button>
        </div>
        {sims === null ? (
          <Loader />
        ) : sims.length === 0 ? (
          <Text variant="secondary" size="sm">
            아직 만든 제품이 없어요.
          </Text>
        ) : (
          <>
            <div className="border-kumo-line overflow-x-auto rounded-lg border">
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>만든 날</Table.Head>
                    <Table.Head>제품</Table.Head>
                    <Table.Head>상태</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pagedSims.slice.map((s) => (
                    <Table.Row key={s.id}>
                      <Table.Cell className="whitespace-nowrap text-xs">
                        {fmtTime(s.createdAt)}
                      </Table.Cell>
                      <Table.Cell>
                        <button
                          type="button"
                          className="text-kumo-link text-left font-medium hover:underline"
                          onClick={() => navigate(`/simulations?open=${s.id}`)}
                        >
                          {s.productName ?? '(이름 없음)'}
                        </button>
                        <div className="text-kumo-subtle text-xs">
                          {productCategoryLabel(s.categoryId)}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          <Badge
                            variant={
                              s.status === 'done'
                                ? 'success'
                                : s.status === 'failed'
                                  ? 'error'
                                  : 'info'
                            }
                          >
                            {s.status}
                          </Badge>
                          {s.gatePassed === false && <Badge variant="warning">best-of</Badge>}
                          {s.isDeleted && <Badge variant="outline">삭제됨</Badge>}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
            <TablePagination
              page={pagedSims.page}
              setPage={pagedSims.setPage}
              perPage={pagedSims.perPage}
              total={pagedSims.total}
            />
          </>
        )}
      </div>

      <PaymentLedgerPanel userId={user.userId} onEntitlements={setEnt} />

      <ConfirmedEventsPanel userId={user.userId} />

      <div className="border-kumo-line flex flex-wrap items-center justify-between gap-2 border-t pt-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRevoke('quote')}>
            안 쓴 견적 지급분 회수
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setRevoke('generations')}
          >
            안 쓴 생성 지급분 회수
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={<Trash />}
          disabled={busy}
          onClick={() => setDeleteOpen(true)}
        >
          계정 삭제
        </Button>
      </div>

      <GrantDialog
        open={grantOpen}
        busy={busy}
        onGrant={(g) => void grant(g)}
        onClose={() => setGrantOpen(false)}
      />
      <MessageDialog
        open={messageOpen}
        heading="사용자에게 메시지"
        description="앱의 알림 탭에 남고 푸시가 가요."
        busy={busy}
        onSend={(m) => void message(m)}
        onClose={() => setMessageOpen(false)}
      />
      <ConfirmDialog
        open={revoke !== null}
        title="안 쓴 지급분을 회수할까요?"
        description="어드민이 지급한 것 중 아직 쓰지 않은 것만 회수해요. 스토어에서 산 것은 건드리지 않아요."
        confirmLabel="회수"
        destructive
        busy={busy}
        onConfirm={() => void doRevoke()}
        onClose={() => setRevoke(null)}
      />
      <DeleteResource
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        resourceType="계정"
        resourceName={user.email || user.userId}
        deleteButtonText="계정 삭제"
        isDeleting={busy}
        errorMessage={error ?? undefined}
        onDelete={() => remove()}
      />
    </div>
  )
}

/**
 * 사용자 · 어드민 — 이메일로 사용자를 찾아 플랜·이번 기간 사용량·만든 제품을 보고, 크레딧 지급·메시지·계정 삭제를 한다.
 * 플랜은 RevenueCat 스냅샷 (조회 전용 — 무료 부여는 RevenueCat 의 Grant entitlement).
 */
export function UsersPage() {
  const getToken = useGetToken()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [found, setFound] = useState<AdminUserView[] | null>(null)
  const [admins, setAdmins] = useState<AdminEntry[] | null>(null)
  const pagedAdmins = usePagedList(admins)
  const [adminEmail, setAdminEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removeAdminTarget, setRemoveAdminTarget] = useState<string | null>(null)
  /** 계정 삭제 뒤 안내 (카드는 사라지므로 여기서) */
  const [deleteNotice, setDeleteNotice] = useState<{ ok: boolean; text: string } | null>(null)

  const reload = useCallback(async () => {
    try {
      setAdmins((await adminApi.users.admins(getToken)).items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  // 견적·대시보드에서 ?id= 로 넘어온 경우 바로 그 사용자를 연다
  const idParam = params.get('id')
  useEffect(() => {
    if (!idParam) return
    let alive = true
    adminApi.users
      .byId(getToken, idParam)
      .then((r) => alive && setFound([r.user]))
      .catch((err: unknown) => alive && setError(toMessage(err)))
    return () => {
      alive = false
    }
  }, [getToken, idParam])

  const search = async () => {
    const q = email.trim()
    if (!q) return
    setBusy(true)
    setDeleteNotice(null)
    try {
      setFound((await adminApi.users.lookup(getToken, q)).items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const changeAdmin = async (target: string, make: boolean) => {
    const e = target.trim().toLowerCase()
    if (!e) return
    setBusy(true)
    try {
      const { items } = make
        ? await adminApi.users.addAdmin(getToken, e)
        : await adminApi.users.removeAdmin(getToken, e)
      setAdmins(items)
      setAdminEmail('')
      setFound(
        (prev) =>
          prev?.map((u) =>
            u.email === e
              ? { ...u, isAdmin: make, adminSource: make ? ('table' as const) : null }
              : u,
          ) ?? prev,
      )
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
      setRemoveAdminTarget(null)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="사용자"
        description="사용자를 찾아 플랜·사용량·제품을 보고, 크레딧 지급·메시지·계정 삭제를 해요. 어드민 권한도 여기서 관리해요."
      />

      <HelpPanel
        id="users"
        steps={[
          <>
            이메일로 찾으세요. [견적 요청] 의 <b>사용자 카드</b> 버튼이나 대시보드의 사용자 ID 를
            눌러도 바로 열려요.
          </>,
          <>
            <b>크레딧 지급</b> — 견적이 늦었거나 결과가 잘못 나왔을 때 보상으로 견적 크레딧이나 생성
            횟수를 줘요. 사유는 꼭 적으세요(작업 기록에 남아요).
          </>,
          <>
            <b>메시지</b> — 그 사용자에게만 앱 알림 + 푸시를 보내요.
          </>,
          <>
            <b>계정 삭제</b> — 사용자가 요청했을 때만. 제품·글·개인정보가 지워지고 되돌릴 수 없어요.
            확인 창에 이메일을 그대로 입력해야 해요.
          </>,
        ]}
        notes={[
          '플랜(Basic·Pro)은 여기서 못 바꿔요. 무료로 주려면 RevenueCat 대시보드 → Customer → Grant entitlement 를 쓰세요. 몇 초 뒤 자동 반영돼요.',
          '회수는 어드민이 지급한 것 중 안 쓴 것만 돼요. 스토어 구매분은 건드리지 않아요.',
          '구독 중인 계정을 삭제해도 스토어 구독은 자동 해지되지 않아요. 사용자에게 스토어에서 해지하라고 안내하세요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}
      {deleteNotice && (
        <Banner variant={deleteNotice.ok ? 'default' : 'alert'} title={deleteNotice.text} />
      )}

      {/* ── 사용자 조회 ── */}
      <section className="flex flex-col gap-3">
        <Text variant="heading">사용자 찾기</Text>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-64 flex-1">
              <Input
                label="이메일"
                placeholder="someone@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  e.key === 'Enter' && void search()
                }
              />
            </div>
            <Button
              variant="primary"
              icon={<MagnifyingGlass />}
              loading={busy}
              disabled={!email.trim()}
              onClick={() => void search()}
            >
              찾기
            </Button>
          </div>

          {found !== null && found.length === 0 && (
            <Empty
              icon={<UserCircle size={40} className="text-kumo-inactive" />}
              title="그 이메일의 사용자가 없어요"
              description="앱에 한 번이라도 로그인해야 여기서 찾을 수 있어요."
            />
          )}
          {found?.map((u) => (
            <UserCard
              key={u.userId}
              user={u}
              onAdminChange={(target, make) =>
                make ? changeAdmin(target, true) : Promise.resolve(setRemoveAdminTarget(target))
              }
              onDeleted={(id, warnings) => {
                setFound((prev) => prev?.filter((x) => x.userId !== id) ?? prev)
                setDeleteNotice(
                  warnings.includes('revenuecat')
                    ? {
                        ok: false,
                        text: `계정은 지웠어요. RevenueCat 구독자 기록은 지우지 못했어요 — RevenueCat 대시보드에서 사용자 id ${id} 를 지워 주세요.`,
                      }
                    : { ok: true, text: '계정을 지웠어요.' },
                )
              }}
            />
          ))}
        </Surface>
      </section>

      {/* ── 어드민 ── */}
      <section className="flex flex-col gap-3">
        <div>
          <Text variant="heading">어드민</Text>
          <Text variant="secondary" size="sm">
            이 어드민에 들어올 수 있는 사람이에요. ADMIN_EMAILS 항목은 서버 설정이라 여기서 못
            지워요.
          </Text>
        </div>
        <Surface className="flex flex-col rounded-lg p-0">
          <div className="flex flex-wrap items-end gap-3 p-6 pb-4">
            <div className="min-w-64 flex-1">
              <Input
                label="이메일로 추가"
                placeholder="teammate@example.com"
                value={adminEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminEmail(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  e.key === 'Enter' && void changeAdmin(adminEmail, true)
                }
              />
            </div>
            <Button
              variant="secondary"
              icon={<ShieldCheck />}
              disabled={busy || !adminEmail.trim()}
              onClick={() => void changeAdmin(adminEmail, true)}
            >
              어드민 추가
            </Button>
          </div>
          {admins === null ? (
            <div className="flex items-center gap-3 p-6">
              <Loader />
              <Text variant="secondary">불러오는 중…</Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>이메일</Table.Head>
                    <Table.Head>출처</Table.Head>
                    <Table.Head>추가한 날</Table.Head>
                    <Table.Head>
                      <span className="sr-only">작업</span>
                    </Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pagedAdmins.slice.map((a) => (
                    <Table.Row key={a.email}>
                      <Table.Cell className="font-medium">{a.email}</Table.Cell>
                      <Table.Cell>
                        {a.source === 'env' ? (
                          <Badge variant="outline">ADMIN_EMAILS</Badge>
                        ) : (
                          <span className="text-kumo-subtle text-xs">
                            추가: <span className="font-mono">{a.addedBy.slice(0, 14)}…</span>
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{fmtDate(a.createdAt)}</Table.Cell>
                      <Table.Cell className="text-right">
                        {a.source === 'table' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<ShieldSlash />}
                            disabled={busy}
                            onClick={() => setRemoveAdminTarget(a.email)}
                          >
                            해제
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </Surface>
        <TablePagination
          page={pagedAdmins.page}
          setPage={pagedAdmins.setPage}
          perPage={pagedAdmins.perPage}
          total={pagedAdmins.total}
        />
      </section>

      <ConfirmDialog
        open={removeAdminTarget !== null}
        title="어드민 권한을 해제할까요?"
        description={`${removeAdminTarget ?? ''} 은(는) 더 이상 이 어드민에 들어올 수 없어요.`}
        confirmLabel="해제"
        destructive
        busy={busy}
        onConfirm={() => removeAdminTarget && void changeAdmin(removeAdminTarget, false)}
        onClose={() => setRemoveAdminTarget(null)}
      />
    </div>
  )
}
